import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';
import { Typography, useMediaQuery, useTheme, Grid } from '@mui/material';
import AppFormatAmount from '../../../../app/features/format/AppFormatAmount';

import { useTranslation } from 'react-i18next';
import investmentKpiLang from '../../langs/investmentKpiLang';
import langInvestWordLang from '../../langs/langInvestWordLang';
import { CircleRounded } from '@mui/icons-material';
import CAppPaper from '../../../../app/components/CAppPaper';
import IDashboardFundType from '../../interfaces/dashboard/IDashboardFundType';
import styleVariables from '../../../../app/styles/styleVariables';
import { useAppSettings } from '../../../../app/features/settings/providers/AppSettingsProvider';

import am5locales_en_US from '@amcharts/amcharts5/locales/en_US';
import am5locales_fr_FR from '@amcharts/amcharts5/locales/fr_FR';
import useAppFormatAmount from '../../../../app/features/format/hooks/useAppFormatAmount';
import useAppFormatNumber from '../../../../app/features/format/hooks/useAppFormatNumber';
import useInvestExpertises from '../../hooks/expertise/useInvestExpertises';
import { TInvestFundCurrency } from '../../types/fund/TInvestFundCurrency';
import CGraphMultiCurrency from '../CGraphMultiCurrency';
import AppLoaderGraph from '../../../../app/features/loader/graph/AppLoaderGraph';
import CGraphNoFund from '../CGraphNoFund';

interface IChartDataCommitted {
	fundType: string;
	tooltipSettings: {
		tooltipText: string;
	};
	committedCapital: number;
	sliceColor: string;
}

interface IChartDataCalled {
	fundType: string;
	tooltipSettings: {
		tooltipText: string;
	};
	calledCapital: number;
	sliceColor: string;
	settings?: {
		opacity: number;
	};
	tickSettings?: {
		opacity: number;
	};
}

interface ILegendData {
	name: string;
	committedCapital: number;
	calledCapital: number;
	color: string;
}

type CGraphTotalCommittedCapitalProps = {
	fundTypes: IDashboardFundType[];
};

const CGraphTotalCommittedCapital = (props: CGraphTotalCommittedCapitalProps) => {
	const { fundTypes } = props;

	const { t, i18n } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { rounding } = useAppSettings();
	const { displayRounding, displayRoundingSuffix, displayCurrencySymbol } = useAppFormatAmount();
	const { formatPercent } = useAppFormatNumber();
	const investExpertises = useInvestExpertises();

	const darkModeEnabled = theme.palette.mode === 'dark';

	const [totalCommittedCapital, setTotalCommittedCapital] = useState(0);
	const [percentCommittedCapital, setPercentCommittedCapital] = useState(0);
	const [chartDataCommitted, setChartDataCommitted] = useState<IChartDataCommitted[]>([]);
	const [chartDataCalled, setChartDataCalled] = useState<IChartDataCalled[]>([]);

	const [legendData, setLegendData] = useState<ILegendData[]>([]);

	const [currency, setCurrency] = useState<TInvestFundCurrency>(undefined);
	useEffect(() => {
		if (fundTypes) {
			const currencies = _.uniqBy(fundTypes, 'currency').map((x) => x.currency);
			setCurrency(currencies && currencies.length === 1 ? currencies[0] : null);
		}
	}, [fundTypes]);

	useEffect(() => {
		const listColors = !darkModeEnabled
			? styleVariables.sliceColors.lightMode.map((x) => am5.color(x))
			: styleVariables.sliceColors.darkMode.map((x) => am5.color(x));
		if (fundTypes && investExpertises) {
			const totalCommittedCapital = _.sumBy(fundTypes, 'committedCapital');
			setTotalCommittedCapital(totalCommittedCapital);

			setPercentCommittedCapital(
				_.round(_.sumBy(fundTypes, 'calledCapital') / _.sumBy(fundTypes, 'committedCapital'), 2),
			);

			const dataCalled = [] as IChartDataCalled[];
			const dataCommitted = [] as IChartDataCommitted[];
			const legendDataTmp = [] as ILegendData[];
			fundTypes.forEach((fundType: IDashboardFundType, index: number) => {
				const mapFundTypesCurrent = investExpertises.find((x) => x.tag === fundType.tag);
				const ratioCommited = _.round(fundType.committedCapital / totalCommittedCapital, 2);
				dataCommitted.push({
					fundType: t(mapFundTypesCurrent?.name ?? ''),
					tooltipSettings: {
						tooltipText: `{category} : ${formatPercent(ratioCommited)}% ${t(
							langInvestWordLang.committed.tag,
						)}`,
					},
					committedCapital: displayRounding(fundType.committedCapital, rounding),
					sliceColor: listColors[(index + 1) % listColors.length].toString(),
				});

				legendDataTmp.push({
					name: t(mapFundTypesCurrent?.name ?? ''),
					committedCapital: displayRounding(fundType.committedCapital, rounding),
					calledCapital: displayRounding(fundType.calledCapital, rounding),
					color: listColors[(index + 1) % listColors.length].toString(),
				});

				setLegendData(legendDataTmp);

				// CALLED
				dataCalled.push({
					fundType: t(mapFundTypesCurrent?.name ?? ''),
					tooltipSettings: {
						tooltipText: `{category} : {value} ${displayRoundingSuffix(rounding)}${displayCurrencySymbol(
							currency,
						)} ${t(langInvestWordLang.called.tag)}`,
					},
					calledCapital: displayRounding(fundType.calledCapital, rounding),
					sliceColor: listColors[(index + 1) % listColors.length].toString(),
				});
				dataCalled.push({
					fundType: t(mapFundTypesCurrent?.name ?? ''),
					tooltipSettings: {
						tooltipText: '',
					},
					calledCapital: displayRounding(fundType.committedCapital - fundType.calledCapital, rounding),
					sliceColor: darkModeEnabled
						? theme.palette.background.paper.toString()
						: theme.palette.secondary.dark.toString(),
					settings: {
						opacity: 0,
					},
					tickSettings: {
						opacity: 0,
					},
				});
			});
			setChartDataCalled(dataCalled);
			setChartDataCommitted(dataCommitted);
		}
	}, [
		fundTypes,
		t,
		darkModeEnabled,
		formatPercent,
		displayRounding,
		rounding,
		displayRoundingSuffix,
		theme.palette.background.paper,
		theme.palette.secondary.dark,
		investExpertises,
		displayCurrencySymbol,
		currency,
	]);

	useEffect(() => {
		if (chartDataCalled.length > 0 && chartDataCommitted.length > 0 && currency) {
			const root = am5.Root.new('invest-dashboard-capital-chart');
			if (darkModeEnabled) {
				root.setThemes([am5themes_Dark.new(root), am5themes_Responsive.new(root)]);
			} else {
				root.setThemes([am5themes_Animated.new(root), am5themes_Responsive.new(root)]);
			}

			if (i18n.language === 'fr') {
				root.locale = am5locales_fr_FR;
			} else {
				root.locale = am5locales_en_US;
			}

			const chart = root.container.children.push(
				am5percent.PieChart.new(root, {
					layout: root.verticalLayout,
					innerRadius: am5.percent(60),
				}),
			);

			// CALLED
			const calledCapitalSerie = chart.series.push(
				am5percent.PieSeries.new(root, {
					valueField: 'calledCapital',
					categoryField: 'fundType',
					alignLabels: true,
				}),
			);

			calledCapitalSerie?.get('colors')?.set(
				'colors',
				chartDataCalled.map((x: IChartDataCalled) => am5.color(x.sliceColor)),
			);

			calledCapitalSerie.ticks.template.setAll({ forceHidden: true });
			calledCapitalSerie.labels.template.setAll({ forceHidden: true });
			calledCapitalSerie.slices.template.setAll({
				templateField: 'tooltipSettings',
			});
			calledCapitalSerie.slices.template.states.create('hover', { scale: 0.95 });

			const sliceCalledCapital = calledCapitalSerie.slices.template;
			sliceCalledCapital.set('toggleKey', 'none');

			sliceCalledCapital.states.create('active', {
				shiftRadius: 0,
			});

			calledCapitalSerie.data.setAll(chartDataCalled);
			calledCapitalSerie.appear(1000, 100);

			// COMMITTED
			const committedCapitalSerie = chart.series.push(
				am5percent.PieSeries.new(root, {
					valueField: 'committedCapital',
					categoryField: 'fundType',
					alignLabels: true,
					innerRadius: am5.percent(60),
				}),
			);

			committedCapitalSerie?.get('colors')?.set(
				'colors',
				chartDataCommitted.map((x: IChartDataCommitted) => am5.color(x.sliceColor)),
			);

			const committedTicksTemplate = committedCapitalSerie.ticks.template;
			committedTicksTemplate.setAll({
				templateField: 'tickSettings',
			});

			committedTicksTemplate.setAll({
				stroke: am5.Color.fromString('#fff'),
			});

			committedCapitalSerie.slices.template.setAll({
				templateField: 'tooltipSettings',
			});

			committedCapitalSerie.labels.template.setAll({
				text: `{category} : {value} ${displayRoundingSuffix(rounding)}${displayCurrencySymbol(currency)}`,
				fill: am5.Color.fromString('#fff'),
				templateField: 'settings',
			});

			const sliceCommittedCapital = committedCapitalSerie.slices.template;

			sliceCommittedCapital.setAll({
				tooltipText: `{category} : {value} ${displayRoundingSuffix(rounding)}${displayCurrencySymbol(
					currency,
				)} ${t(langInvestWordLang.committed.tag)}`,
			});

			sliceCommittedCapital.set('toggleKey', 'none');
			sliceCommittedCapital.states.create('active', {
				shiftRadius: 0,
			});

			committedCapitalSerie.data.setAll(chartDataCommitted);

			committedCapitalSerie.appear(1000, 100);

			chart.seriesContainer.children.push(
				am5.Label.new(root, {
					textAlign: 'center',
					y: 32,
					centerY: am5.percent(100),
					fill: am5.Color.fromString('#fff'),
					centerX: am5.p50,
					text: `[bold fontSize:30px]${_.round(percentCommittedCapital * 100, 0)}%[/]\n[fontSize:16px]${t(
						investmentKpiLang.capital_called.tag,
					)}[/]`,
				}),
			);

			return () => {
				root.dispose();
			};
		}
	}, [
		chartDataCommitted,
		chartDataCalled,
		darkModeEnabled,
		percentCommittedCapital,
		t,
		isMobile,
		rounding,
		i18n.language,
		displayRoundingSuffix,
		displayCurrencySymbol,
		currency,
	]);

	return (
		<CAppPaper variant="outlined" color="secondary" displayBackgroundImage>
			<Typography
				id="invest-dashboard-capital-title"
				variant="h2"
				component="h2"
				sx={{ textAlign: 'center', color: 'white' }}
			>
				{t(investmentKpiLang.total_committed_capital.tag)}{' '}
				<AppFormatAmount value={totalCommittedCapital} currency={currency} />
			</Typography>

			{currency === undefined || fundTypes === undefined ? (
				<AppLoaderGraph height={300} />
			) : fundTypes && fundTypes.length === 0 ? (
				<CGraphNoFund height={300} />
			) : currency !== null ? (
				<>
					<div
						id="invest-dashboard-capital-chart"
						style={{ height: '300px', margin: '0px', padding: '0px' }}
					/>

					{isMobile ? (
						<div style={{ marginTop: '30px' }}>
							{legendData &&
								legendData.map((legend, key) => {
									return (
										<Grid
											container
											spacing={3}
											key={key}
											style={{ marginBottom: '10px' }}
											justifyContent="center"
											alignItems="top"
										>
											<Grid item xs={1}>
												<CircleRounded
													style={{ color: legend.color, fontSize: '1rem', margin: '0px' }}
												/>
											</Grid>
											<Grid item xs={11}>
												<div style={{ color: 'white', padding: '0px', margin: '0px' }}>
													<div
														style={{
															fontSize: '1rem',
															lineHeight: '1.2rem',
															fontWeight: 'bold',
															padding: '0px',
															margin: '0px',
														}}
													>
														{legend.name}
													</div>
													<div style={{ fontSize: '0.85rem', fontStyle: 'normal' }}>
														{legend.committedCapital}
														{displayRoundingSuffix(rounding)}
														{displayCurrencySymbol(currency)}{' '}
														{t(langInvestWordLang.committed.tag)} | {legend.calledCapital}{' '}
														{displayRoundingSuffix(rounding)}
														{displayCurrencySymbol(currency)}{' '}
														{t(langInvestWordLang.called.tag)}
													</div>
												</div>
											</Grid>
										</Grid>
									);
								})}
						</div>
					) : (
						''
					)}
				</>
			) : (
				<CGraphMultiCurrency height={300} />
			)}
		</CAppPaper>
	);
};

export default CGraphTotalCommittedCapital;
